/**
 * [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
 */

import { romanQualities, romanToInt } from 'shared/definitions/musicTheory/romanNumerals';
import type { InversionNumber, Figure } from 'shared/definitions/musicTheory/chords';
import Chord from './chord';
import KeySignature from './keySignature';
// import NoteHolder from './noteHolder';
import utils from '../utils';
import * as Font from '../uTheory.Font/font';
import type Note from './note';

/**
 *
 * ## romanNumeral.ts
 *
 * An object for representing a roman numeral (a chord within a key).
 *
 * Initialize in any of these ways:
 * ```typescript
 *    // key, roman, figures:
 *    new RomanNumeral("Bb/M", "ii", "42");
 *
 *    // key, roman with figures
 *    new RomanNumeral("G/m", "IV6");
 *
 *    // key:roman with figures
 *    new RomanNumeral("Eb:viio6");
 *    new RomanNumeral("C/m:V42");
 *    new RomanNumeral("D/m: iv65");
 * ```
 *
 * Examples:
 * ```typescript
 *    ii = new romanNumeral("C#", "ii42");
 *    ii.root; // D#
 *    ii.bass; // C#
 *    ii.key; // C# Major
 *    ii.numeral; // ii
 *    ii.figures; // 42
 *
 *    ii.setInversion(1);
 *    ii.setFigures(65)
 *    ii.setKey(Eb);
 *
 *    Or:
 *    var key = "G#/min", numeral = "III", figures = "6";
 *    var III = ii;
 *    III.set(key, numeral, figures);
 * ```
 */
export default class RomanNumeral {
  private _chord: Chord = new Chord();

  private _isSeventh: boolean;

  private _scaleDegree: string;

  private _numeral: string;

  private _key: KeySignature;

  private _inversion: InversionNumber;

  private _figures: Figure;

  private _root: Note;

  //  private _scale: Scale;

  /**
   * @param {string} romanNumeralName in format "Cmin:V42"
   */
  constructor(romanNumeralName: string);
  /**
   * @param {string | KeySignature} key
   * @param {string} numeral "ii"
   * @param {string | number} figures "42" "643" etc...
   */
  constructor(key: string | KeySignature, numeral: string, figures?: string | number);
  constructor(key: string | KeySignature, numeral?: string, figures?: string | number) {
    if (typeof numeral === 'undefined') {
      if (utils.isString(key) && key.includes(':')) {
        this._setKey(key.split(':')[0]);
        this.set(key.split(':')[1], figures);
        return;
      }
      throw new Error(
        `${key} is not a valid roman numeral name. Format should be Key:Numeral, as Cmin:V42`
      );
    }

    this._setKey(key);
    this.set(numeral, figures);
  }

  /**
   * Set the numeral and figures for this RN
   * @param {String} numeral "vi", etc...
   * @param {String} figures "42", etc...
   * @returns {this}
   */
  set(romanNumeralWithFigures: string): this;
  set(numeral: string, figures?: string | number): this;
  set(numeral: string, figures?: string | number): this {
    // Let's be sure we don't have any figures hanging out in there:
    if (typeof figures === 'undefined') {
      const nArray = numeral.split('');
      numeral = '';
      figures = '';
      // Split the numeral and figures
      nArray.forEach((n) => {
        if (['i', 'I', 'v', 'V', 'o', '/'].includes(n)) {
          numeral += n;
        } else if (n > '0' && n < '9') {
          figures += n;
        } else if (['#', 'b'].includes(n)) {
          if (!numeral.length) {
            numeral += n;
          } else {
            figures += n;
          }
        }
      });
    }
    this._setNumeral(numeral);
    this._setFigures(figures);
    this._updateParts();
    return this;
  }

  /*
   *  =================== String Output Methods ======================
   *
   *   For displaying the names of the roman numeral in various ways.
   *
   *  ================================================================
   */

  /**
   * Return a string reprsentation of the RN in various formats
   * @param {Object} options
   * @param {boolean} options.font format in accidental font html
   * @param {boolean} options.key include key signature
   * @param {boolean} toAccidentalFont default false, return formatted?
   * @returns {string} string representation of RN
   */
  toString(options: { font?: boolean; key?: boolean } = {}) {
    if (options.font) {
      return this.toAccidentalFontString({ key: options.key });
    }

    const keyString = options.key ? `${this.key}: ` : '';
    return keyString + this.numeral + this.figures;
  }

  /**
   * Returns an html html formatted string representation of the RN
   * @param {Object} options
   * @param {boolean} options.key include key sig in output
   * @returns {string} V/43/, etc...
   */
  toAccidentalFontString({ key = false } = {}) {
    const dimRegEx = /o[/]{0,1}/;
    const isDim = dimRegEx.exec(this.numeral) as ('o' | 'o/')[];
    const dim = isDim ? Font.toHTML('quality', isDim[0]) : '';
    const degreePortion = this.numeral.split('').filter((c) => ['i', 'I', 'v', 'V'].includes(c));
    const deg = Font.toHTML('romanNumeral', degreePortion as [Font.FontKey]);

    const figures = this.chord.figuresToFont(this.figures);
    const keyString = key ? `${this.key.toAccidentalFontString()}: ` : '';
    return `${keyString}${deg}${dim}${figures}`;
  }

  get isValid() {
    return (
      RomanNumeral.isValidNumeral(this.numeral, this.key.quality === 'Major', this.isSeventh) ||
      RomanNumeral.isValidMixture(this.numeral, this.key.quality === 'Major', this.isSeventh)
    );
  }

  get isMixture() {
    return (
      !RomanNumeral.isValidNumeral(this.numeral, this.key.quality === 'Major', this.isSeventh) &&
      RomanNumeral.isValidMixture(this.numeral, this.key.quality === 'Major', this.isSeventh)
    );
  }

  removeMixture() {
    const scaleDegree = this.scaleDegree === '#7' ? '7' : this.scaleDegree;
    this.setNumeral(romanQualities[this.key.quality][scaleDegree][this.isSeventh ? 1 : 0]);
  }

  get isSeventh() {
    return this._isSeventh;
  }

  get scaleDegree() {
    return this._scaleDegree;
  }

  get root() {
    return this._root;
  }

  get numeral() {
    return this._numeral;
  }

  setNumeral(numeral: string) {
    this._setNumeral(numeral);
    this._updateParts();
  }

  get figures(): Figure {
    return this._figures;
  }

  setFigures(figures: number | string) {
    this._setFigures(figures);
    this._updateParts();
  }

  get key() {
    return this._key;
  }

  setKey(key: KeySignature | string) {
    this._setKey(key);
    this._updateParts();
  }

  get inversion() {
    return this._inversion;
  }

  setInversion(inversion: 0 | 1 | 2 | 3) {
    this._setInversion(inversion);
    this._updateParts();
  }

  get chord() {
    this._throw('get chord');
    return this._chord;
  }

  get third() {
    this._throw('get third');
    return this._chord.third;
  }

  get fifth() {
    this._throw('get fifth');
    return this._chord.fifth;
  }

  get seventh() {
    this._throw('get seventh');
    return this._chord.seventh;
  }

  get bass() {
    this._throw('get bass');
    return this._chord.bass;
  }

  get noteHolder() {
    this._throw('get noteHolder');
    return this._chord.noteHolder;
  }

  /*
   * ======================== "private" functions =======================
   *
   *
   * ====================================================================
   */

  /** @private */
  private _throw(methodName = 'perform action') {
    if (!this.isValid)
      throw new Error(
        `Cannot ${methodName} on an invalid roman numeral: ${this.key}:${this.toString()}.`
      );
  }

  /** @private */
  private _setKey(key) {
    this._key = KeySignature.stringToKey(key);
  }

  /** @private */
  private _setNumeral(numeral: string) {
    const roman = numeral
      .split('')
      .filter((c) => ['v', 'V', 'i', 'I'].includes(c))
      .join('');
    this._numeral = numeral;
    let scaleDegree = romanToInt(roman);
    let acc = numeral
      .slice(0, 1)
      .split('')
      .filter((c) => ['#', 'b'].includes(c))
      .join('');
    // if we're in minor, and this is a vii, then it's root is #7
    if (this.key.quality === 'minor' && roman === 'vii') {
      acc = '#';
      scaleDegree = 7;
    }
    this._scaleDegree = acc + scaleDegree;
  }

  /** @private */
  private _setInversion(inversionInt: 0 | 1 | 2 | 3) {
    this._chord.setInversion(inversionInt);
    this._inversion = this._chord.inversion;
    this._figures = Chord.inversionToFigures(this._inversion, this._isSeventh);
  }

  /** @private */
  private _setFigures(figures: string | number) {
    this._inversion = Chord.figuresToInversion(figures);
    this._isSeventh = Chord.figuresAreSeventh(figures);
    this._figures = Chord.inversionToFigures(this._inversion, this._isSeventh);
  }

  /** @private */
  private _updateParts() {
    // Needs key, numeral, figures & inversion to be up to date.

    // Updates the root:
    const scale = this.isMixture ? this._key.getParallelScale() : this._key.getScale();
    this._root = scale[this._scaleDegree];

    // Update the chord:
    this._updateChord();
  }

  /** @private */
  private _updateChord() {
    const rootNumber = Number(
      `${this._scaleDegree}`
        .split('')
        .filter((c) => c > '0' && c < '9')
        .join('')
    );

    const scale = this.isMixture ? this._key.getParallelScale() : this._key.getScale();
    const minor =
      (this.isMixture && this._key.quality === 'Major') ||
      (!this.isMixture && this._key.quality === 'minor');

    let rootNote = scale[`${rootNumber}`];
    if (minor && rootNumber === 7 && (this._numeral === 'V' || this._numeral.includes('vii'))) {
      rootNote = scale.M7;
    }
    // console.log('rootNumber', rootNumber);
    // console.log('rootNote', rootNote);

    const qualityAccessor = `${minor ? 'minor' : 'Major'}ChordQualities`;
    const qualityTable = romanQualities[qualityAccessor];
    // console.log('qualityTable', qualityTable);
    const chordQuality = qualityTable[rootNumber][this._isSeventh ? 1 : 0];

    // console.log('chordQuality', chordQuality);
    const chord = new Chord(rootNote, chordQuality);
    this._chord = chord;
    this._chord.setInversion(this.inversion);
  }

  /**
   * Is a given roman numeral string valid in Major or minor?
   * @param {string} numeral "vi", "viio" etc...
   * @param {boolean} major = true;
   * @returns {boolean} isValid
   */
  static isValidNumeral(numeral: string, major = true, isSeventh: boolean) {
    const valids = romanQualities[major ? 'Major' : 'minor'];
    numeral = numeral
      .split('')
      .filter((c) => ['v', 'V', 'i', 'I', 'o', '/'].includes(c))
      .join('');

    return !!utils.find(valids, (rnByDeg) => rnByDeg[isSeventh ? 1 : 0] === numeral);
  }

  /**
   * Valid mixture includes iio, iio/, iv, VI, III from minor, I from major.
   * @param {string} numeral "vi", "viio" etc...
   * @param {boolean} major = true;
   * @returns {boolean} true if valid mixture.
   */
  static isValidMixture(numeral: string, major = true, isSeventh: boolean) {
    numeral = numeral
      .split('')
      .filter((c) => ['b', 'V', 'v', 'I', 'i', 'o', '/'].includes(c))
      .join('');
    if (major && isSeventh) {
      return ['iio/', 'iv', 'bVI', 'bIII', 'viio'].includes(numeral);
    }
    if (major) {
      ['iio', 'iio', 'iv', 'bVI', 'bIII'].includes(numeral);
    }
    if (!major && isSeventh) {
      return ['IV'].includes(numeral);
    }
    if (!major) {
      return ['I', 'IV'].includes(numeral);
    }

    return (major ? ['iio', 'iio/', 'iv', 'bVI', 'bIII', 'viio'] : ['I']).includes(numeral);
  }
}
